import type { MouseEvent, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { StatusIndicator } from '@interstate/components/StatusIndicator';
import { Tabs } from '@interstate/components/Tabs';
import { ToggleGroup } from '@interstate/components/ToggleGroup';
import { BuildingOfficeIcon } from '@interstate/components/Icons/BuildingOfficeIcon';
import { UserIcon } from '@interstate/components/Icons/UserIcon';
import { UsersIcon } from '@interstate/components/Icons/UsersIcon';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';

import { BUSINESS, BUSINESS_DETAILS, BUYER, BUYER_DETAILS, CO_BUYER, CO_BUYER_DETAILS, INSURANCE } from '../customerCard/constants';

type TabErrorKey = typeof BUYER | typeof CO_BUYER | typeof INSURANCE;
import { CustomerTabsContainer, ToggleLabelWithIcon } from '../header/EditCustomer.styles';

export type CustomerTabsCustomerKey = typeof BUYER | typeof CO_BUYER;
export type CustomerTabsSubTabKey = typeof BUYER_DETAILS | typeof CO_BUYER_DETAILS | typeof INSURANCE;

export interface CustomerTabsProps {
    activeCustomer: CustomerTabsCustomerKey;
    activeSubTab: CustomerTabsSubTabKey;
    onCustomerChange: (next: CustomerTabsCustomerKey) => void;
    onSubTabActivate: (next: CustomerTabsSubTabKey) => void;
    isBusinessBuyer?: boolean;
    errorEntities?: Partial<Record<TabErrorKey, boolean>>;
}

interface SubTabItem {
    value: CustomerTabsSubTabKey;
    label: ReactNode;
    component: JSX.Element;
    active: boolean;
}

const wrapWithErrorDot = (entityName: TabErrorKey, hasError: boolean, children: ReactNode): ReactNode =>
    hasError ? (
        <StatusIndicator data-testid={`error-badge-${entityName}`} variant="dot">
            {children}
        </StatusIndicator>
    ) : (
        children
    );

export const CustomerTabs = ({
    activeCustomer,
    activeSubTab,
    onCustomerChange,
    onSubTabActivate,
    isBusinessBuyer = false,
    errorEntities
}: CustomerTabsProps): JSX.Element => {
    const hasCoBuyer = useSelector(offerReduxSelectors.hasCoBuyer);
    const buyerLabel = isBusinessBuyer ? BUSINESS : 'Buyer';
    const buyerDetailsLabel = isBusinessBuyer ? BUSINESS_DETAILS : 'Buyer Details';
    const BuyerIcon = isBusinessBuyer ? BuildingOfficeIcon : UserIcon;

    const effectiveCustomer: CustomerTabsCustomerKey = hasCoBuyer ? activeCustomer : BUYER;

    const handleToggleChange = (_event: MouseEvent, value: string) => {
        onCustomerChange(value as CustomerTabsCustomerKey);
    };

    const handleSubTabActivate = (value: string) => {
        onSubTabActivate(value as CustomerTabsSubTabKey);
    };

    const hasBuyerError = errorEntities?.[BUYER] === true;
    const hasCoBuyerError = errorEntities?.[CO_BUYER] === true;
    const hasInsuranceError = errorEntities?.[INSURANCE] === true;

    const subTabs: SubTabItem[] =
        effectiveCustomer === BUYER
            ? [
                  {
                      value: BUYER_DETAILS,
                      label: wrapWithErrorDot(BUYER, hasBuyerError, buyerDetailsLabel),
                      component: <></>,
                      active: activeSubTab === BUYER_DETAILS
                  },
                  {
                      value: INSURANCE,
                      label: wrapWithErrorDot(INSURANCE, hasInsuranceError, 'Insurance'),
                      component: <></>,
                      active: activeSubTab === INSURANCE
                  }
              ]
            : [
                  {
                      value: CO_BUYER_DETAILS,
                      label: wrapWithErrorDot(CO_BUYER, hasCoBuyerError, 'Co-Buyer Details'),
                      component: <></>,
                      active: true
                  }
              ];

    return (
        <CustomerTabsContainer data-testid="customer-tabs">
            {hasCoBuyer && (
                <ToggleGroup
                    data-testid="customer-tabs-toggle"
                    onChange={handleToggleChange}
                    toggleButtonOptions={[
                        {
                            selected: effectiveCustomer === BUYER,
                            'data-testid': 'customer-tabs-toggle-buyer',
                            'aria-label': 'Buyer',
                            children: wrapWithErrorDot(
                                BUYER,
                                hasBuyerError,
                                wrapWithErrorDot(
                                    INSURANCE,
                                    hasInsuranceError,
                                    <ToggleLabelWithIcon>
                                        <BuyerIcon data-testid="customer-tabs-toggle-buyer-icon" aria-hidden="true" />
                                        <span>{buyerLabel}</span>
                                    </ToggleLabelWithIcon>
                                )
                            ),
                            value: BUYER
                        },
                        {
                            selected: effectiveCustomer === CO_BUYER,
                            'data-testid': 'customer-tabs-toggle-coBuyer',
                            'aria-label': 'Co-Buyer',
                            children: wrapWithErrorDot(
                                CO_BUYER,
                                hasCoBuyerError,
                                <ToggleLabelWithIcon>
                                    <UsersIcon data-testid="customer-tabs-toggle-coBuyer-icon" aria-hidden="true" />
                                    <span>Co-Buyer</span>
                                </ToggleLabelWithIcon>
                            ),
                            value: CO_BUYER
                        }
                    ]}
                />
            )}
            <Tabs
                data-testid="customer-tabs-sub-tabs"
                id="customer-tabs-sub-tabs"
                onActivate={handleSubTabActivate}
                tabs={subTabs}
            />
        </CustomerTabsContainer>
    );
};

export default CustomerTabs;
